import React, {useContext, useState} from 'react';
import gql from 'graphql-tag';
import {useQuery} from '@apollo/client';
import {EmailNotificationTemplate, EmailTemplateType, PlatformEventKind} from '../../../schema';
import Skeleton from '../../../ui/skeleton/Skeleton';
import If from '../../../components/utils/If';
import {AccountContext} from "../../../utils/Context";
import ReactDocumentTitle from "../../../components/ReactDocumentTitle";
import cn from "classnames";
import {createUseStyles} from "react-jss";
import EmailNotification from "../../../components/emailTemplate/EmailNotification";
import NotFoundScene from "../../system/NotFoundScene";
import UILink from "../../../ui/UILink";
import PrimaryButton from "../../../ui/PrimaryButton";

interface GetEmailTemplatesQuery {
  templates: EmailNotificationTemplate[];
}

export default function EmailTemplatesScene() {
  const account = useContext(AccountContext);
  const styles = legacyStyles();

  const {loading, data, error} = useQuery<GetEmailTemplatesQuery>(query, {
    variables: {accountId: account?.id},
    errorPolicy: 'all'
  });
  const [addMode, setAddMode] = useState(false);

  if (!account) {
    return <NotFoundScene/>;
  }

  const onSuccess = () => {
    // do nothing
  };
  const allowedEvents = [PlatformEventKind.Attendee_Registered,
    PlatformEventKind.Certificate_Generated,
    PlatformEventKind.Evaluation_Created,
    PlatformEventKind.Event_Started,
    PlatformEventKind.Event_Finished];

  return (
    <>
      <ReactDocumentTitle title="Configure email templates"/>
      <div className={cn('flex flex-row justify-between border-b pb-4', styles.borderColor)}>
        <h2 className="text-2xl text-gray-dark font-medium">Email Templates</h2>
      </div>
      <div className="py-6">
        <If condition={loading}>
          <Skeleton className="w-4/5 mb-4"/>
          <Skeleton className="w-2/5"/>
        </If>
        <If condition={!!data}>
          <p className="pb-6">
            Company-wide email templates to send emails to attendees when certain actions occur. You can configure these
            templates once and apply them for all your events.
          </p>
          <p className="pb-6">
            You can use tags to personalize the email in both subject and body. The full list of tags is available
            <UILink href="https://support.workshopbutler.com/article/21-email-tags"> here</UILink>.
          </p>
          <div className="grid gap-y-4">
            {data?.templates.map(template => (
              <EmailNotification key={template.id} objectId={account?.id.toString()} template={template}
                                 refetchQueries={['GetEmailTemplatesQuery', 'templates']}
                                 objectType={EmailTemplateType.account_event}
                                 allowedEvents={allowedEvents}
                                 onSuccess={onSuccess} dark={true} editableSettings={true} deletable={true}/>
            ))}
            <If condition={addMode}>
              <EmailNotification objectId={account?.id.toString()} onCancel={() => setAddMode(false)}
                                 allowedEvents={allowedEvents}
                                 refetchQueries={['GetEmailTemplatesQuery', 'templates']}
                                 objectType={EmailTemplateType.account_event}
                                 onSuccess={() => setAddMode(false)}/>
            </If>
            <If condition={!addMode}>
              <PrimaryButton className="mt-3" onClick={() => setAddMode(true)}> Add notification</PrimaryButton>
            </If>
          </div>
        </If>
      </div>
    </>
  );
}

const legacyStyles = createUseStyles(
  {
    borderColor: {
      borderColor: '#e7e7e7'
    }
  }
);

const query = gql`
  query GetEmailTemplatesQuery($accountId: ID!) {
    templates: emailTemplates(objectId: $accountId, objectType: account_event) {
      id
      name
      subject
      body
      conditions
      {
        event
        when
        {
          units
          unitType
          beforeAfter
        }
      }
    }
  }
`;
